<template lang="pug">
  v-app
    BasePageLoadingProgress(:value="isAppLoading && _isMounted")
    BaseSpinner(:value="isAppLoading && !isAppLoadedFirst && !__hydration" zIndex="999" full opaque)
    TheAnalytics
    TheToaster(ref="toaster")
    TheModal(ref="modal")
    TheHeader(v-if="isShowHeaderAndFooter")
    acre-cookie-consent(
      :domain="CONFIG.urls.frontend"
      link-href="/company/privacy#we-use-following-tools"
    )
    template(v-if="isLoggedIn && !isSignUpPage")
      BannerInvestmentDocuments
      BannerEmailVerification
      TheMarketMessages
    BannerMaintenance
    v-main
      router-view
      NavigationNotificationMenu(v-if="isLoggedIn")
    TheFooter(v-if="isShowFooter && isShowHeaderAndFooter")
</template>

<script>
import analytics from 'Analytics';
import { mapActions, mapGetters } from 'vuex';
import { INACTIVE_LOGOUT_MESSAGE } from 'Config/constans';
import toasterMixin from 'Mixins/toasterMixin';
import maintenanceMixin from 'Mixins/maintenanceMixin';
import { authWSClient } from 'WSClient';
import CONFIG from 'Config';
import { getSearchParamBySearch } from 'Utils/url';
import BasePageLoadingProgress from 'Components/Base/BasePageLoadingProgress';
import BaseSpinner from 'Components/Base/BaseSpinner';
import BaseButtonAddEvent from 'Components/Base/BaseButtonAddEvent';
import TheToaster from 'Components/TheToaster/TheToaster';
import TheAnalytics from 'Components/TheAnalytics/TheAnalytics';
import TheHeader from 'Components/TheHeader/TheHeader';
import TheFooter from 'Components/TheFooter/TheFooter';
import { setCookie, getCookie } from 'Utils/session';
import TheModal from 'Components/TheModal/TheModal';
import /* webpackChunkName: ‘AcreCookieConsent’ */ 'AcreComponents/AcreCookieConsent/AcreCookieConsent.vue3.umd';
import store, { registerStoreModuleAsync } from './store';

const BannerInvestmentDocuments = () => import(/* webpackChunkName: 'BannerInvestmentDocuments' */ 'Components/BannerInvestmentDocuments/BannerInvestmentDocuments');
const NavigationNotificationMenu = () => import(/* webpackChunkName: 'NavigationNotificationMenu' */ 'Components/NavigationNotificationMenu/NavigationNotificationMenu');
const BannerEmailVerification = () => import(/* webpackChunkName: 'BannerEmailVerification' */ 'Components/BannerEmailVerification/BannerEmailVerification');
const BannerMaintenance = () => import(/* webpackChunkName: 'BannerMaintenance' */ 'Components/BannerMaintenance/BannerMaintenance');
const TheMarketMessages = () => import(/* webpackChunkName: 'TheMarketMessages' */ 'Components/TheMarketMessages/TheMarketMessages');

export default {
  name: 'App',
  components: {
    BaseSpinner,
    BasePageLoadingProgress,
    TheAnalytics,
    TheToaster,
    TheHeader,
    TheFooter,
    NavigationNotificationMenu,
    BaseButtonAddEvent,
    BannerInvestmentDocuments,
    BannerEmailVerification,
    BannerMaintenance,
    TheModal,
    TheMarketMessages,
  },
  mixins: [toasterMixin, maintenanceMixin],
  data() {
    return {
      isLoadModal: false,
      inactiveLogoutMessageId: null,
      isAppLoadedFirst: false,
    };
  },
  computed: {
    ...mapGetters('login', [
      'isLoggedIn',
      'tokenRefreshRequired',
      'tokenExpired',
      'logoutLoading',
    ]),
    ...mapGetters('router', [
      'routeLoading',
    ]),
    ...mapGetters('profile', [
      'profileModel',
    ]),
    isAppLoading() {
      return !this.__prerender && (this.routeLoading || this.logoutLoading);
    },
    isSignUpPage() {
      return (this.$route.name || '').startsWith('sign-up');
    },
    isShowHeaderAndFooter() {
      const routeExclusionList = ['reset-password', 'sign-in', 'sign-up', 'sign-up-social', 'sign-up-complete', 'sign-up-accreditation'];
      const currentName = this.$route.name;
      return !routeExclusionList.includes(currentName);
    },
    isShowFooter() {
      return this.isAppLoadedFirst || this.__prerender;
    },
  },
  watch: {
    isLoggedIn: {
      handler() {
        if (this.isLoggedIn) {
          authWSClient.open();
          this.handleGeneralLoginDependencies();
        } else authWSClient.close();
      },
      immediate: true,
    },
    async tokenExpired(isExpired) {
      if (isExpired) {
        this.inactiveLogoutMessageId = this.$_toaster_info(INACTIVE_LOGOUT_MESSAGE, false);
      } else {
        this.closeInactiveLogoutMessage();
      }
    },
    tokenRefreshRequired(tokenRefreshRequired) {
      if (tokenRefreshRequired) this.setUserActivityListeners();
      else this.removeUserActivityListeners();
    },
    '$route.name': {
      handler: 'setBodyPageClass',
      immediate: true,
    },
    isAppLoading(isAppLoading) {
      if (!isAppLoading) this.isAppLoadedFirst = true;
    },
  },
  mounted() {
    this.clearPreload();
    this.setRouter(this.$router);
    this.setViewDimensions();
    this.setAffiliateId();
    window.addEventListener('resize', this.setViewDimensions);
    document.addEventListener('DOMNodeInserted', this.domNodeInsertedEventListener);
    window.addEventListener('click', this.clickEventListener);
    // Load TheModal module timeout
    setTimeout(this.loadModalModule, 15000);
    // Check for GCLID in URL, if present, set cookie
    this.setGclidCookie();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setViewDimensions);
    document.removeEventListener('DOMNodeInserted', this.domNodeInsertedEventListener);
    window.removeEventListener('click', this.clickEventListener);
    this.removeUserActivityListeners();
  },
  methods: {
    ...mapActions('router', [
      'setRouter',
      'getSuperUserStatus',
    ]),
    ...mapActions('login', [
      'checkToken',
    ]),
    ...mapActions('view', [
      'setScreenWidth',
      'setScreenHeight',
      'setPortraitOrientation',
    ]),
    ...mapActions('profile', [
      'updateMetaProfileData',
    ]),
    async handleGeneralLoginDependencies() {
      // get success investment list for banner with farm document
      store.dispatch('investment/getInvestmentDocumentsOnce');
      // get account list for BannerUpdatedOnboarding
      await registerStoreModuleAsync('account');
      store.dispatch('account/loadAccountListOnce');
    },
    setViewDimensions() {
      this.setScreenWidth(document.documentElement.clientWidth);
      this.setScreenHeight(document.documentElement.clientHeight);
      this.setPortraitOrientation(this.getIsPortraitOrientation());
    },
    closeInactiveLogoutMessage() {
      this.$_toaster_hide(this.inactiveLogoutMessageId);
    },
    clearPreload() {
      const spinner = document.getElementById('preload-spinner');
      if (spinner) spinner.style.display = 'none';
    },
    setUserActivityListeners() {
      document.addEventListener('mousemove', this.checkTokenOnActive);
      window.addEventListener('scroll', this.checkTokenOnActive);
    },
    removeUserActivityListeners() {
      document.removeEventListener('mousemove', this.checkTokenOnActive);
      window.removeEventListener('scroll', this.checkTokenOnActive);
    },
    checkTokenOnActive() {
      this.checkToken();
      // Remove listeners to avoid duplication calls
      this.removeUserActivityListeners();
    },
    getIsPortraitOrientation() {
      const orientation = window.matchMedia('(orientation: portrait)');
      return orientation.matches;
    },
    /**
     * Set affiliate id to store to do not send affiliate data to service if user is not affiliated
     */
    setAffiliateId() {
      // Define if this function is ready on app
      window.__checkAffiliateIdFromStore = true;
      const affiliateId = getSearchParamBySearch('affiliate_id', window.location.search);
      if (affiliateId) localStorage.setItem('affiliateId', affiliateId);
    },
    setBodyPageClass() {
      document.body.removeAttribute('class');
      document.body.classList.add(`body-page-${this.$route.name}`);
    },
    clickEventListener(e) {
      if (this.routeLoading || e.target.href) {
        analytics.triggerLinkClicked({ href: e.target.href, baseURI: e.target.baseURI, text: e.target.outerText });
      }
    },
    domNodeInsertedEventListener(e) {
      if (!e.target || !e.target.className || typeof e.target.className.includes === 'undefined') return;
      if (e.target.className.includes('leadinModal')) {
        analytics.triggerPromotionViewed();
      }
    },
    setGclidCookie() {
      const gclid = getSearchParamBySearch('gclid', window.location.search);
      if (gclid && !getCookie('gclid')) {
        setCookie('gclid', gclid, 90, '/');
      }
    },
  },
  staticData: {
    CONFIG,
  },
};
</script>

<style lang="sass">
@import 'styles/app.scss'
#chat-widget-container, #hubspot-messages-iframe-container
  z-index: 8 !important
</style>
